import { Star } from "lucide-react";
import { cn } from "@/lib/utils";

interface GoogleRatingBadgeProps {
  rating: number;
  reviewCount: number;
  googleMapsUrl?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const GoogleRatingBadge = ({ 
  rating, 
  reviewCount, 
  googleMapsUrl, 
  size = 'md',
  className 
}: GoogleRatingBadgeProps) => {
  const sizeClasses = {
    sm: {
      container: 'p-3',
      stars: 'w-4 h-4',
      rating: 'text-xl',
      count: 'text-xs',
      link: 'text-xs'
    },
    md: {
      container: 'p-4',
      stars: 'w-5 h-5',
      rating: 'text-2xl',
      count: 'text-sm',
      link: 'text-sm'
    },
    lg: {
      container: 'p-5',
      stars: 'w-6 h-6',
      rating: 'text-3xl',
      count: 'text-base',
      link: 'text-base'
    }
  };

  const sizes = sizeClasses[size];

  // Calculate filled stars (supports fractional like 4.7)
  const fullStars = Math.floor(rating);
  const partialFill = rating - fullStars;
  const emptyStars = 5 - Math.ceil(rating);

  const reviewText = reviewCount === 1 ? 'Google Review' : 'Google Reviews';

  const content = (
    <div 
      className={cn(
        "rounded-xl bg-white/10 border border-white/20 backdrop-blur-sm",
        "hover:border-[#ffdb4d]/50 hover:bg-white/15 transition-all duration-300",
        "group cursor-pointer",
        sizes.container,
        className
      )}
    >
      {/* Stars and Rating Row */}
      <div className="flex items-center gap-3 mb-2">
        {/* 5-Star Display */}
        <div className="flex items-center gap-0.5">
          {/* Full stars */}
          {Array.from({ length: fullStars }).map((_, i) => (
            <Star 
              key={`full-${i}`} 
              className={cn(
                sizes.stars, 
                "text-[#ffdb4d] fill-[#ffdb4d]",
                "group-hover:scale-110 transition-transform duration-200"
              )}
              style={{ transitionDelay: `${i * 50}ms` }}
            />
          ))}
          
          {/* Partial star */}
          {partialFill > 0 && (
            <div className="relative">
              {/* Empty star behind */}
              <Star className={cn(sizes.stars, "text-[#ffdb4d]/30")} />
              {/* Filled portion on top */}
              <div 
                className="absolute top-0 left-0 overflow-hidden"
                style={{ width: `${partialFill * 100}%` }}
              >
                <Star className={cn(sizes.stars, "text-[#ffdb4d] fill-[#ffdb4d]")} />
              </div>
            </div>
          )}
          
          {/* Empty stars */}
          {Array.from({ length: emptyStars }).map((_, i) => (
            <Star 
              key={`empty-${i}`} 
              className={cn(sizes.stars, "text-[#ffdb4d]/30")} 
            />
          ))}
        </div>

        {/* Rating Number */}
        <span className={cn(sizes.rating, "font-bold text-white")}>
          {rating.toFixed(1)}
        </span>
      </div>

      {/* Review Count */}
      <div className="flex items-center justify-between">
        <span className={cn(sizes.count, "text-white/70 font-medium")}>
          {reviewCount.toLocaleString()} {reviewText}
        </span>
        
        {googleMapsUrl && (
          <span className={cn(
            sizes.link, 
            "text-[#ffdb4d] font-semibold",
            "group-hover:underline flex items-center gap-1"
          )}>
            View on Google
            <span className="group-hover:translate-x-0.5 transition-transform">→</span>
          </span>
        )}
      </div>
    </div>
  );

  if (googleMapsUrl) {
    return (
      <a 
        href={googleMapsUrl} 
        target="_blank" 
        rel="noopener noreferrer"
        className="block"
      >
        {content}
      </a>
    );
  }

  return content;
};

export default GoogleRatingBadge;
